/**
 * Website / URL widget — shared config schema, validation, sanitization
 * and provider normalization.
 *
 * This module is the single source of truth for how a "website" zone is
 * shaped and, crucially, how untrusted operator input (URLs and pasted
 * <iframe> embed code) is made safe *before* it is ever stored or
 * rendered. The backend re-implements the same rules in PHP
 * (App\Services\WebEmbedSanitizer) — keep the two in sync.
 *
 * Security contract (must match the backend):
 *   - Only http:// and https:// sources are ever accepted; https is
 *     preferred and http is upgraded where a provider is https-only.
 *   - javascript:, data:, file:, blob:, vbscript:, about: and bare
 *     filesystem paths are rejected.
 *   - Pasted embed code is NEVER stored or rendered as raw HTML. We
 *     extract only the iframe `src` plus a small allow-list of safe
 *     attributes (title, allow, allowfullscreen, width, height).
 *   - <script>, srcdoc, inline event handlers and unknown attributes
 *     are discarded.
 */

export type WebsiteDisplayMode =
  | "website"
  | "direct_video"
  | "iframe_video"
  | "iframe_map"
  | "iframe_embed";

export type WebsiteInputType = "url" | "embed";

export type WebsiteFit = "fit" | "fill" | "stretch" | "native";

export type AutoScrollSpeed = "off" | "slow" | "medium" | "fast" | "custom";
export type AutoScrollDirection = "down" | "up" | "left" | "right";
export type AutoScrollEndBehavior = "stop" | "restart" | "reverse";
export type UserAgentMode = "desktop" | "mobile";

export const WEBSITE_DISPLAY_MODES: Array<{ value: WebsiteDisplayMode; label: string }> = [
  { value: "website",       label: "Website" },
  { value: "direct_video",  label: "Direct Video URL" },
  { value: "iframe_video",  label: "Embedded Video / iFrame" },
  { value: "iframe_map",    label: "Embedded Map / iFrame" },
  { value: "iframe_embed",  label: "Generic Embedded Content" },
];

export const REFRESH_INTERVALS: Array<{ value: number; label: string }> = [
  { value: 0,  label: "Never" },
  { value: 1,  label: "Every 1 minute" },
  { value: 5,  label: "Every 5 minutes" },
  { value: 15, label: "Every 15 minutes" },
  { value: 30, label: "Every 30 minutes" },
  { value: 60, label: "Every 60 minutes" },
];

export const WEBSITE_FITS: Array<{ value: WebsiteFit; label: string }> = [
  { value: "fit",     label: "Fit (show whole page, may letterbox)" },
  { value: "fill",    label: "Fill (cover zone, may crop)" },
  { value: "stretch", label: "Stretch (fill exactly, may distort)" },
  { value: "native",  label: "Native / Scroll (recommended for full pages)" },
];

export const AUTO_SCROLL_SPEEDS: Array<{ value: AutoScrollSpeed; label: string }> = [
  { value: "off",    label: "Off" },
  { value: "slow",   label: "Slow" },
  { value: "medium", label: "Medium" },
  { value: "fast",   label: "Fast" },
  { value: "custom", label: "Custom (px / sec)" },
];

export const AUTO_SCROLL_DIRECTIONS: Array<{ value: AutoScrollDirection; label: string }> = [
  { value: "down",  label: "Down" },
  { value: "up",    label: "Up" },
  { value: "left",  label: "Left" },
  { value: "right", label: "Right" },
];

export const AUTO_SCROLL_END_BEHAVIORS: Array<{ value: AutoScrollEndBehavior; label: string }> = [
  { value: "stop",    label: "Stop at end" },
  { value: "restart", label: "Restart from beginning" },
  { value: "reverse", label: "Reverse direction" },
];

/** Preset delay/pause options (milliseconds). `-1` selects the custom field. */
export const AUTO_SCROLL_DELAYS: Array<{ value: number; label: string }> = [
  { value: 0,     label: "0 seconds" },
  { value: 2000,  label: "2 seconds" },
  { value: 5000,  label: "5 seconds" },
  { value: 10000, label: "10 seconds" },
  { value: -1,    label: "Custom" },
];

/** Non-custom auto-scroll speeds in CSS pixels per second. */
export const AUTO_SCROLL_PPS: Record<Exclude<AutoScrollSpeed, "off" | "custom">, number> = {
  slow: 18,
  medium: 45,
  fast: 110,
};

/** Resolve the effective pixels-per-second for a config (0 → no motion). */
export const autoScrollPps = (cfg: WebsiteConfig): number => {
  if (!cfg.auto_scroll_enabled || cfg.auto_scroll_speed === "off") return 0;
  if (cfg.auto_scroll_speed === "custom") {
    const v = Number(cfg.auto_scroll_custom_speed);
    return Number.isFinite(v) && v > 0 ? Math.min(2000, v) : 0;
  }
  return AUTO_SCROLL_PPS[cfg.auto_scroll_speed] ?? 0;
};

/** Full, defaulted config persisted in a website zone's widget_config. */
export interface WebsiteConfig {
  display_mode: WebsiteDisplayMode;
  input_type: WebsiteInputType;
  /** Sanitized, render-ready source. Always http(s) or empty. */
  url: string;
  refresh_interval: number; // minutes; 0 = never
  fit: WebsiteFit;
  background: string;
  // Video-ish options (direct_video + iframe_video)
  muted: boolean;
  autoplay: boolean;
  loop: boolean;
  controls: boolean;
  start_time: number | null;
  allow_fullscreen: boolean;
  // Website / generic
  show_scrollbars: boolean;
  allow_interaction: boolean;
  // Auto-scroll (unattended signage). See autoScrollPps() for the
  // effective speed. Scrolling is done by translating the iframe element
  // within its clipping zone, so it works even for cross-origin sites
  // whose internal document JS cannot be reached.
  auto_scroll_enabled: boolean;
  auto_scroll_speed: AutoScrollSpeed;
  auto_scroll_custom_speed: number; // px/sec, used when speed === "custom"
  auto_scroll_direction: AutoScrollDirection;
  auto_scroll_end_behavior: AutoScrollEndBehavior;
  auto_scroll_start_delay_ms: number;
  auto_scroll_end_pause_ms: number;
  // Advanced viewport controls. null viewport dimensions → match the zone.
  viewport_width: number | null;
  viewport_height: number | null;
  page_zoom: number;          // percent, 100 = no zoom
  user_agent_mode: UserAgentMode;
  initial_scroll_position: number; // px from the start edge
  // Map
  allow_zoom: boolean;
  show_controls: boolean;
  /** Optional iframe title extracted from pasted embed code. */
  title: string | null;
}

export const defaultWebsiteConfig = (): WebsiteConfig => ({
  display_mode: "website",
  input_type: "url",
  url: "",
  refresh_interval: 0,
  fit: "fit",
  background: "#000000",
  muted: true,
  autoplay: true,
  loop: true,
  controls: true,
  start_time: null,
  allow_fullscreen: false,
  show_scrollbars: false,
  allow_interaction: false,
  auto_scroll_enabled: false,
  auto_scroll_speed: "off",
  auto_scroll_custom_speed: 40,
  auto_scroll_direction: "down",
  auto_scroll_end_behavior: "restart",
  auto_scroll_start_delay_ms: 0,
  auto_scroll_end_pause_ms: 0,
  viewport_width: null,
  viewport_height: null,
  page_zoom: 100,
  user_agent_mode: "desktop",
  initial_scroll_position: 0,
  allow_zoom: true,
  show_controls: true,
  title: null,
});

/** Merge an arbitrary stored config onto the defaults so older/partial
 *  rows always read back as a complete WebsiteConfig. */
export const readWebsiteConfig = (cfg: Record<string, any> | null | undefined): WebsiteConfig => ({
  ...defaultWebsiteConfig(),
  ...(cfg ?? {}),
});

export const isIframeMode = (m: WebsiteDisplayMode): boolean =>
  m === "iframe_video" || m === "iframe_map" || m === "iframe_embed";

export const isVideoMode = (m: WebsiteDisplayMode): boolean =>
  m === "direct_video" || m === "iframe_video";

/* ── URL validation ───────────────────────────────────────────────── */

const BLOCKED_SCHEMES = ["javascript:", "data:", "file:", "blob:", "vbscript:", "about:", "mailto:", "tel:"];

/** True only for a syntactically valid, http(s) absolute URL. */
export const isValidWebUrl = (raw: string): boolean => {
  const s = (raw ?? "").trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  if (BLOCKED_SCHEMES.some((p) => lower.startsWith(p))) return false;
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return false;
  }
  if (u.protocol !== "http:" && u.protocol !== "https:") return false;
  if (!u.hostname || u.hostname.length < 3 || !u.hostname.includes(".")) return false;
  return true;
};

/** Validation message for the current input, or null when valid/empty. */
export const validateWebsiteInput = (cfg: WebsiteConfig, rawInput: string): string | null => {
  const s = (rawInput ?? "").trim();
  if (!s) return null; // empty is "not yet configured", not an error
  if (cfg.input_type === "embed") {
    const src = extractIframeSrc(s);
    if (!src) return "This embed code contains unsupported or unsafe content.";
    if (!isValidWebUrl(src)) return "Enter a valid iframe URL or supported embed code.";
    return null;
  }
  // URL input. A YouTube link whose id we can't extract is a malformed
  // watch URL — refuse it rather than try to load a broken embed.
  if (isYouTubeUrl(s) && !extractYouTubeId(s)) return "Enter a valid YouTube video URL.";
  const normalized = normalizeProviderUrl(s, cfg.display_mode);
  if (!isValidWebUrl(normalized)) {
    return isIframeMode(cfg.display_mode)
      ? "Enter a valid iframe URL or supported embed code."
      : "Enter a valid website URL beginning with http:// or https://.";
  }
  return null;
};

/* ── iframe embed-code extraction ─────────────────────────────────── */

const SAFE_IFRAME_ATTRS = new Set(["src", "title", "allow", "allowfullscreen", "width", "height"]);

/**
 * Extract only the `src` URL from pasted <iframe> embed code. Uses the
 * DOM parser so we get robust attribute handling, but we read back ONLY
 * the src and never re-serialise the operator's markup. Returns null
 * when there's no iframe or the src is unsafe.
 */
export const extractIframeSrc = (embedCode: string): string | null => {
  const s = (embedCode ?? "").trim();
  if (!s) return null;
  // If they pasted a bare URL rather than an <iframe>, accept it.
  if (!/<iframe/i.test(s)) {
    return isValidWebUrl(s) ? s : null;
  }
  if (/<script/i.test(s) || /srcdoc\s*=/i.test(s) || /\son\w+\s*=/i.test(s)) {
    // Explicitly unsafe markers — refuse rather than try to clean.
    // A legitimate provider iframe never needs these.
    return null;
  }
  try {
    const doc = new DOMParser().parseFromString(s, "text/html");
    const iframe = doc.querySelector("iframe");
    if (!iframe) return null;
    const src = iframe.getAttribute("src") || "";
    return isValidWebUrl(src) ? src : null;
  } catch {
    // Fallback regex if DOMParser is unavailable.
    const m = s.match(/<iframe[^>]*\ssrc\s*=\s*["']([^"']+)["']/i);
    const src = m?.[1] ?? "";
    return isValidWebUrl(src) ? src : null;
  }
};

/** Pull safe, supported attributes out of pasted embed code. */
export const extractIframeMeta = (embedCode: string): { title: string | null; allowFullscreen: boolean } => {
  const out = { title: null as string | null, allowFullscreen: false };
  try {
    const doc = new DOMParser().parseFromString(embedCode, "text/html");
    const iframe = doc.querySelector("iframe");
    if (!iframe) return out;
    for (const attr of Array.from(iframe.attributes)) {
      if (!SAFE_IFRAME_ATTRS.has(attr.name.toLowerCase())) continue;
      if (attr.name.toLowerCase() === "title") out.title = attr.value || null;
      if (attr.name.toLowerCase() === "allowfullscreen") out.allowFullscreen = true;
    }
  } catch { /* ignore */ }
  return out;
};

/* ── YouTube detection ────────────────────────────────────────────── */

const YOUTUBE_HOSTS = new Set(["youtube.com", "youtube-nocookie.com", "youtu.be"]);
const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;

const youtubeHost = (u: URL): string =>
  u.hostname.replace(/^www\./, "").replace(/^m\./, "").toLowerCase();

/**
 * True for any recognised YouTube link — watch, youtu.be, shorts, embed,
 * /v/, /live/ and youtube-nocookie, regardless of extra query params.
 * A YouTube URL must never be rendered with a native <video> element, so
 * this is what the inspector uses to force embedded-video mode.
 */
export const isYouTubeUrl = (raw: string): boolean => {
  const s = (raw ?? "").trim();
  if (!s) return false;
  try { return YOUTUBE_HOSTS.has(youtubeHost(new URL(s))); } catch { return false; }
};

/**
 * Extract the 11-character video id from any recognised YouTube URL form,
 * or null when this isn't a YouTube URL / no valid id can be found. Never
 * touches the network — pure URL parsing.
 */
export const extractYouTubeId = (raw: string): string | null => {
  const s = (raw ?? "").trim();
  if (!s) return null;
  let u: URL;
  try { u = new URL(s); } catch { return null; }
  const host = youtubeHost(u);
  if (!YOUTUBE_HOSTS.has(host)) return null;

  let id = "";
  if (host === "youtu.be") {
    id = u.pathname.replace(/^\//, "").split("/")[0] ?? "";
  } else {
    const v = u.searchParams.get("v");
    if (v) {
      id = v;
    } else {
      const m = u.pathname.match(/^\/(?:embed|shorts|v|live)\/([^/?#]+)/);
      if (m) id = m[1];
    }
  }
  return YOUTUBE_ID_RE.test(id) ? id : null;
};

/** True for a real browser-playable media URL (the only thing that should
 *  ever go through Direct Video URL mode / a native <video> element). */
export const isDirectMediaUrl = (url: string): boolean =>
  /\.(mp4|webm|ogg|ogv|m3u8)(\?|#|$)/i.test((url ?? "").trim());

/* ── Provider normalization ───────────────────────────────────────── */

/**
 * Convert a normal provider page URL into its official embed URL where
 * we can do so purely by URL rewriting (no scraping / no network). If we
 * don't recognise the provider we return the input unchanged so a valid
 * generic embed URL still works.
 */
export const normalizeProviderUrl = (raw: string, mode: WebsiteDisplayMode): string => {
  const s = (raw ?? "").trim();
  if (!s) return s;
  let u: URL;
  try { u = new URL(s); } catch { return s; }

  const host = u.hostname.replace(/^www\./, "").toLowerCase();

  // YouTube (all forms) → https://www.youtube.com/embed/{id}
  const ytId = extractYouTubeId(s);
  if (ytId) return `https://www.youtube.com/embed/${ytId}`;

  // Vimeo → https://player.vimeo.com/video/{id}
  if (host === "vimeo.com") {
    const id = u.pathname.split("/").filter(Boolean)[0];
    if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
  }
  if (host === "player.vimeo.com") return s;

  // Google Maps — only accept an actual embed URL. A normal maps share
  // link can't be turned into an embed without an API key, so we leave
  // it and let validation/the provider decide (X-Frame-Options).
  if (mode === "iframe_map" && (host === "google.com" || host === "maps.google.com")) {
    if (u.pathname.includes("/maps/embed")) return s;
  }

  return s;
};

/* ── Sandbox / allow computation for rendering ────────────────────── */

/**
 * The minimum iframe `sandbox` token set for a given config. We always
 * withhold `allow-top-navigation` and `allow-popups` (unless a mode
 * genuinely needs a popup) so the embedded page can never navigate the
 * host app. `allow-same-origin` is intentionally omitted for generic
 * embeds so the framed page can't reach the parent's storage/cookies —
 * but many providers (YouTube/Vimeo/Maps) require it to function, so we
 * grant it for those recognised modes.
 */
export const iframeSandbox = (cfg: WebsiteConfig): string => {
  // allow-same-origin is required for provider players/maps to load and
  // for most sites to function. We deliberately WITHHOLD
  // allow-top-navigation(-by-user-activation) in every case so the embed
  // can never navigate the host display app.
  const tokens = new Set<string>(["allow-scripts", "allow-forms", "allow-same-origin"]);
  if (cfg.allow_interaction) tokens.add("allow-pointer-lock");
  if (cfg.allow_fullscreen) tokens.add("allow-popups"); // some players open fs via popup gesture
  return Array.from(tokens).join(" ");
};

/** The `allow` (feature-policy) attribute for the iframe. Video players
 *  always advertise autoplay/encrypted-media/PiP so the player toggles can
 *  take effect; fullscreen only when the operator enabled it. */
export const iframeAllow = (cfg: WebsiteConfig): string => {
  const feats: string[] = [];
  if (isVideoMode(cfg.display_mode)) {
    feats.push("autoplay", "encrypted-media", "picture-in-picture");
  }
  if (cfg.allow_fullscreen) feats.push("fullscreen");
  if (cfg.display_mode === "iframe_map") feats.push("geolocation");
  return feats.join("; ");
};

/**
 * Build the render-time src, applying autoplay/mute/loop/controls query
 * params for the known providers so those toggles actually take effect.
 */
export const buildRenderSrc = (cfg: WebsiteConfig): string => {
  const base = cfg.url;
  if (!base || !isVideoMode(cfg.display_mode)) return base;
  let u: URL;
  try { u = new URL(base); } catch { return base; }
  const host = u.hostname.replace(/^www\./, "").toLowerCase();

  if (host.endsWith("youtube.com")) {
    // Autoplay is blocked with sound on virtually every browser/device, so
    // force mute whenever autoplay is on (item mirrors the inspector warning).
    const mute = cfg.autoplay ? true : cfg.muted;
    u.searchParams.set("autoplay", cfg.autoplay ? "1" : "0");
    u.searchParams.set("mute", mute ? "1" : "0");
    u.searchParams.set("controls", cfg.controls ? "1" : "0");
    if (cfg.loop) {
      u.searchParams.set("loop", "1");
      const id = u.pathname.split("/embed/")[1];
      if (id) u.searchParams.set("playlist", id);
    }
    if (cfg.start_time && cfg.start_time > 0) u.searchParams.set("start", String(cfg.start_time));
    u.searchParams.set("playsinline", "1");
    u.searchParams.set("enablejsapi", "1");
    u.searchParams.set("rel", "0");
    // Give YouTube the app origin so the embedded player is authorised and
    // Error 153 (missing/invalid client identification) is avoided. Guarded
    // for non-browser (SSR/test) contexts.
    const origin = typeof window !== "undefined" ? window.location?.origin : "";
    if (origin && /^https?:\/\//.test(origin)) {
      u.searchParams.set("origin", origin);
      u.searchParams.set("widget_referrer", origin);
    }
    return u.toString();
  }
  if (host.endsWith("vimeo.com")) {
    u.searchParams.set("autoplay", cfg.autoplay ? "1" : "0");
    u.searchParams.set("muted", cfg.muted ? "1" : "0");
    u.searchParams.set("loop", cfg.loop ? "1" : "0");
    if (!cfg.controls) u.searchParams.set("controls", "0");
    if (cfg.start_time && cfg.start_time > 0) u.hash = `t=${cfg.start_time}s`;
    return u.toString();
  }
  return base;
};

/** Whether autoplay-with-sound is likely to be blocked by the browser. */
export const autoplayWithSoundRisk = (cfg: WebsiteConfig): boolean =>
  isVideoMode(cfg.display_mode) && cfg.autoplay && !cfg.muted;

/** Direct-video source extension → whether it needs HLS support. */
export const isHlsUrl = (url: string): boolean => /\.m3u8(\?|#|$)/i.test(url ?? "");

/** CSS object-fit / sizing for the chosen fit mode. `native` has no
 *  object-fit meaning for video, so it falls back to contain. */
export const fitToObjectFit = (fit: WebsiteFit): "contain" | "cover" | "fill" =>
  fit === "fill" ? "cover" : fit === "stretch" ? "fill" : "contain";
